import { usePayeeListToast } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/usePayeeListToast';
import { usePreventScroll } from '@/hooks/usePreventScroll';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import {
  Drawer,
  ToastProvider,
  useToast,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useState } from 'react';
import { DetailDrawerContainer } from './DetailDrawerContainer';
import { useConfirmCancelEditModal } from './hooks/useConfirmCancelEditModal';
import { useViewerPayeeIdChange } from './hooks/useViewerPayeeChange';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);

export type Props = Record<string, never>;

export type DetailDrawerProps = {
  open: boolean;
  onClose: () => void;
  apPayeeId: string;
};

const DetailDrawer: FC<DetailDrawerProps> = memo(
  ({ open, onClose, apPayeeId }) => {
    usePayeeListToast();
    const [isEditing, setIsEditing] = useState(false);
    const [isEditFormDirty, setIsEditFormDirty] = useState(false);
    const { confirm, ConfirmModal } = useConfirmCancelEditModal();
    const DEFAULT_DRAWER_DETAIL_WIDTH = 1000;
    const containerRef = useGlobalContainerRef();
    const { t } = useTranslation();

    usePreventScroll(isEditing);

    const handleClose = useCallback(
      async (isCloseWithDirty?: boolean) => {
        if (!isEditing || !isEditFormDirty || isCloseWithDirty) {
          onClose();
          setIsEditing(false);
          setIsEditFormDirty(false);
          return;
        }
        const isConfirmed = await confirm();
        if (isConfirmed) {
          setIsEditing(false);
          setIsEditFormDirty(false);
          onClose();
        }
      },
      [onClose, isEditing, confirm, setIsEditing, isEditFormDirty]
    );

    return (
      <Drawer
        className={cx(styles['detailDrawerContainer'])}
        width={DEFAULT_DRAWER_DETAIL_WIDTH}
        visible={open}
        onClose={() => handleClose(false)}
        destroyOnClose
        title={<div>{t('ap_payee_detail_title')}</div>}
        getContainer={containerRef}
        mask={isEditing}
        maskClosable={false}
      >
        {apPayeeId && (
          <DetailDrawerContainer
            apPayeeId={apPayeeId}
            onClose={handleClose}
            onEditingChange={setIsEditing}
            onEditFormDirtyChange={setIsEditFormDirty}
            onConfirmCancelEdit={confirm}
          />
        )}
        <ConfirmModal />
      </Drawer>
    );
  }
);

DetailDrawer.displayName = 'DetailDrawer';

export const PayeeDetailDrawer: FC<Props> = memo(() => {
  const [toastApi, toastContextHolder] = useToast();
  const { apPayeeId, isOpen, onDrawerClose } = useViewerPayeeIdChange();
  return (
    <ToastProvider value={toastApi} contextHolder={toastContextHolder}>
      <DetailDrawer
        open={isOpen}
        apPayeeId={apPayeeId}
        onClose={onDrawerClose}
      />
    </ToastProvider>
  );
});

PayeeDetailDrawer.displayName = 'PayeeDetailDrawer';
